import { store } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { userProfileActions } from 'features/userProfile';
import { selectModal } from 'layout/redux/selectors';
import { closeButton, privacyPolicyParagraph } from '../constants/sharedHtml';
import leadConstructor from '../services/leadConstructor';
import { postLeadInformationForm } from '../services/taskFlow';
import CommentsTextArea from './commentsTextArea';
import EmailInput from './emailInput';
import InformationButton from './informationButton';
import NameInput from './nameInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';
import RealtorDropdown from './realtorDropdown';
import LegalText from './legalText';

/* Creates a new instance of a Get More Information Form to allow users making an
appointment to tour the selected housing. */
export default class InformationFormPaid {
    constructor() {
        const state = store.getState();
        const userProfile = state?.userProfile || {};
        const { id, guid, type } = selectModal(state);
        this.id = id;
        this.guid = guid;
        this.type = type;
        this.excludeRecommendations = true;
        this.isPersistent = true;
        this.moreInformationButton = new InformationButton();
        this.commentsTextArea = new CommentsTextArea();
        this.emailAddressInput = new EmailInput();
        this.emailAddressInput.value = userProfile.email;
        this.formId = 'informationFormPaid';
        this.fullNameInput = new NameInput();
        this.fullNameInput.value = userProfile.name;
        this.phoneNumberInput = new PhoneInput();
        this.phoneNumberInput.value = userProfile.phone;
        this.postalCodeInput = new ZipInput();
        this.postalCodeInput.value = userProfile.zip;
        this.postalCodeInput.isInternational = userProfile.livesOutside;
        this.realtorDropdown = new RealtorDropdown();
        this.realtorDropdown.value = userProfile.realtorQuestion;
        this.onClickEvent = null;
        this.legalText = new LegalText(privacyPolicyParagraph);
    }

    /**
     * validateNode: Check if the field has a value as expected.
     * @param {Object} node Input to validate
     * @returns A boolean value indicating whether the input is valid or not.
     */
    static validateNode(node) {
        return node.validate();
    }

    /**
     * getFormOpenedAnalyticsData: Gets the object required for the Google Analytics events on form opened.
     * @param {Object} properties Information regarding to the listing.
     * @returns The Google Analtic event information.
     */
    static getFormOpenedAnalyticsData(properties) {
        const customDimensions = {};

        if (properties.marketId) {
            customDimensions[[GoogleAnalyticsCustomDimensions.MarketId().getName()]] = properties.marketId;
        }

        if (properties.marketName && properties.stateAbbreviation) {
            customDimensions[[GoogleAnalyticsCustomDimensions.MarketName().getName()]] = `${properties.marketName}, ${properties.stateAbbreviation}`;
        }

        if (properties.city) {
            customDimensions[[GoogleAnalyticsCustomDimensions.City().getName()]] = properties.city;
        }

        return new GoogleAnalyticsEvent(
            'Lead Events',
            'Search Results CTA Get More Info',
            'Open Form - Get More Info',
            customDimensions);
    }

    /**
     * setHousingData: Sets the housing information for the form.
     * @param {Object} properties Information regarding to the listing.
     */
    setHousingData(properties = {}) {
        this.housingData = properties;
    }

    /**
     * submitBrochureForm: Process to validate and submit the form.
     * @param {event} event Click event.
     */
    async submitInformationForm(event) {
      event.preventDefault();

      const validationFunctions = [
        this.emailAddressInput,
        this.fullNameInput,
        this.postalCodeInput,
        this.phoneNumberInput,
      ];

      this.moreInformationButton.getNode().disabled = 'disabled';

      const results = await Promise.all(
        validationFunctions.map((input) => InformationFormPaid.validateNode(input))
      );

      if (results.some((isValid) => !isValid)) {
        this.moreInformationButton.getNode().disabled = false;
        return;
      }

      const formData = new FormData(this.getForm());
      const leadInformation = Object.fromEntries(formData.entries());

      leadInformation.id = this.id;
      leadInformation.type = `${this.type}-information`;
      leadInformation.excludeRecommendations = this.excludeRecommendations;
      leadInformation.fromPage = this.housingData.fromPage;
      leadInformation.guid = this.guid;

      const leadObject = leadConstructor(leadInformation);
      leadObject.setHousingInformation?.(this.housingData.listingInformation);

      postLeadInformationForm(leadObject);
    }

    /**
     * handleEmailValidationResult: Handles the result of the email validation.
     * @param {Object} isValid The result of the email validation.
     */
    handleEmailValidationResult(isValid) {
      this.moreInformationButton.getNode().disabled = false;
      if (isValid) {
        this.emailAddressInput.setValidState(this.emailAddressInput.getNode());
        this.emailAddressInput.toggleEmailValidationDataAttribute(true);
      } else {
        this.emailAddressInput.setInvalidState(this.emailAddressInput.getNode());
        this.emailAddressInput.toggleEmailValidationDataAttribute(false);
      }
    }

    /**
     * Sets the binding required for the form.
     */
    bindEvents() {
        this.onClickEvent = this.submitInformationForm.bind(this);
        this.phoneNumberInput.bindEvents();
        this.postalCodeInput.bindEvents();
        this.legalText.bindEvents();
        if (this.fullNameInput.value === '') {
            this.fullNameInput.focus();
        }
        this.realtorDropdown.setSelectedValue();
        this.moreInformationButton.getNode().addEventListener('click', this.onClickEvent);
        this.bindChangeEvents();
    }

    /**
     * bindChangeEvents: Get the form inputs then subscribe each OnChangeEvent to store and persist their values
     */
    bindChangeEvents() {
      const changeInputs = [
          this.emailAddressInput,
          this.fullNameInput,
          this.postalCodeInput,
          this.phoneNumberInput,
          this.realtorDropdown,
      ];

      changeInputs.forEach(input => input.getOnChangeEvent().subscribe(data =>
          store.dispatch(userProfileActions.update(data))));
    }

    /**
     * Removes the bindings that should not be still using.
     */
    unbindEvents() {
        this.moreInformationButton.getNode().removeEventListener('click', this.onClickEvent);
        this.legalText.unbindEvents();
    }

    /**
     * getForm: Gets the form container
     * @returns A HTML element related to the form container.
     */
    getForm() {
        return document.getElementById(this.formId);
    }

    /**
     * Gets the HTML structure for the information form paid.
     * @returns The string with the HTML structure.
     */
    getHtml() {
      this.legalText.setReplacements([
        { key: "{0}", value: "Get More Information"},
        { key: "{1}", value: this.housingData.listingInformation?.brandName ?? "the builder" },
      ]);

        return `
    <div class="lead-form form--brochure" data-lead-form data-qa="lead_modal">
      ${closeButton}
      <p class="form__title">Get More Information</p>
      <p class="form__subtitle">
        We will keep you updated in your area of interest, our progress, and much more…
      </p>
      <p></p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="form__field">
            ${this.fullNameInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.emailAddressInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.phoneNumberInput.getHtml()}
          </div>
          <div class="form__field field--split">
            ${this.postalCodeInput.getHtml()}
          </div>
          <div class="form__field agent--field">
            ${this.realtorDropdown.getHtml()}
          </div>
          <div class="form__field">
            ${this.commentsTextArea.getHtml()}
          </div>
          <div class="form__button-bar">
            ${this.moreInformationButton.getHtml()}
          </div>
        </fieldset>        
      </form>
      ${this.legalText.getHtml()}
    </div>`;
    }
}
